<template>
  <section
    v-if="showSection"
    id="doctors"
    class="lpu-doctors-list d-flex flex-column"
    :class="isMobile ? 'ga-4' : 'ga-6'"
    data-qa="lpu_doctors_list"
  >
    <div class="d-flex align-center justify-space-between">
      <h2
        class="lpu-section-title text--text min-w-0"
        :class="isMobile ? 'text-h6' : 'text-h5'"
        :data-count="isMobile ? String(doctorsCount) : doctorsCountLabel"
        data-qa="lpu_doctors_list_title"
      >
        {{ sectionTitle }}
      </h2>
      <a
        v-if="showAllHref && isMobile"
        class="text-body-2 text-decoration-underline primary--text flex-shrink-0"
        :href="showAllHref"
        data-qa="lpu_doctors_list_all_link"
      >
        {{ allLinkLabel }}
      </a>
    </div>

    <DoctorsMtBanner v-if="lpu.uses_mtlink" />

    <DoctorsFilter
      v-if="specialities.length"
      :specialities="specialities"
      :lpu-doctors-url="lpuDoctorsUrl"
    />

    <div
      v-if="!doctors.length"
      class="text-body-1 text-secondary--text text-center pa-6"
      data-qa="lpu_doctors_list_empty"
    >
      {{ tStatic('Врачи не найдены') }}.
    </div>

    <div
      v-else
      class="d-flex flex-column"
      :class="isMobile ? 'ga-3' : 'ga-4'"
      data-qa="lpu_doctors_list_cards"
    >
      <DoctorCard
        v-for="doctor in doctors"
        :key="doctor.id"
        :doctor="doctor"
        :lpu="lpu"
        :clinic-slots="slotsByDoctorId[doctor.id] || {}"
        :is-slots-loading="isSlotsLoading"
        :doctor-page-url="getCardDoctorUrl(doctor)"
        :lpu-not-working-text="lpuNotWorkingText"
      />
    </div>

    <DoctorsShowAllLink
      v-if="showAllHref"
      :href="showAllHref"
      :label="showAllButtonLabel"
    />
  </section>
</template>

<script lang="ts" setup>
import {
  computed,
  onMounted,
} from 'vue'
import { format, isValid, parseISO } from 'date-fns'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import type { LpuDoctor } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/types'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import unwrapList from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/unwrapList'
import declensionByNum from 'src/utils/declensionByNum'
import getDateFnsLocaleByIso from 'src/functions/getDateFnsLocaleByIso'
import {
  PHARMACY_LPU_TYPE_ID,
  SHOW_ALL_THRESHOLD,
  WELLNESS_LPUTYPE,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/constants'
import getDoctorPageUrl from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/getDoctorPageUrl'
import useDoctorsListSchedule from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/composables/useDoctorsListSchedule'
import DoctorsFilter from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/components/DoctorsFilter.vue'
import DoctorCard from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/components/DoctorCard.vue'
import DoctorsMtBanner from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/components/DoctorsMtBanner.vue'
import DoctorsShowAllLink from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/components/DoctorsShowAllLink.vue'

const props = defineProps<{
  lpu: Lpu
}>()

const isMobile = useIsMobile()
const { context, urlLang }: ContextStore = useContextStore()
const { reverseFullL10N } = useURLStore()

const doctors = computed(() => unwrapList<LpuDoctor>(context.doctors))
const specialities = computed(() => unwrapList(context.lpuspec))
const isWellness = computed(() => props.lpu.lputype.translit === WELLNESS_LPUTYPE)
const showSection = computed(() => (
  Boolean(props.lpu.drcnt)
  && props.lpu.lputype.id !== PHARMACY_LPU_TYPE_ID
  && context.has_workplaces !== false
))
const sectionTitle = computed(() => {
  const roleTitle = isWellness.value ? tStatic('Специалисты') : tStatic('Врачи')
  const nameRod = props.lpu.name_rod ? ` ${tDynamic(props.lpu, 'name_rod')}` : ''

  return isMobile.value ? roleTitle : `${roleTitle}${nameRod}`
})
const doctorsCount = computed(() => props.lpu.drcnt || 0)
const doctorsCountLabel = computed(() => {
  const noun = isWellness.value
    ? declensionByNum(doctorsCount.value, ['специалист', 'специалиста', 'специалистов'])
    : declensionByNum(doctorsCount.value, ['врач', 'врача', 'врачей'])

  return `${doctorsCount.value} ${tStatic(noun)}`
})
const allLinkLabel = computed(() => (
  isWellness.value
    ? `${tStatic('Все')} ${tStatic('специалисты')}`
    : `${tStatic('Все')} ${tStatic('врачи')}`
))
const showAllButtonLabel = computed(() => {
  if (isMobile.value) {
    return tStatic('Все врачи')
  }

  return isWellness.value
    ? `${tStatic('Показать всех')} ${tStatic('специалистов')}`
    : `${tStatic('Показать всех')} ${tStatic('врачей')}`
})
const lpuDoctorsUrl = reverseFullL10N({
  name: 'lpu_doctors',
  args: {
    language: urlLang,
    town_translit: props.lpu.town.translit,
    lpu_translit: props.lpu.translit,
  },
})
/**
 * Кнопка «все врачи», если врачей больше порога или на главной только выборка клиники.
 */
const showAllHref = computed(() => {
  const total = doctorsCount.value
  const shown = doctors.value.length

  if (total <= SHOW_ALL_THRESHOLD && shown >= total) {
    return undefined
  }

  return isMobile.value
    ? `${lpuDoctorsUrl}#h1_title`
    : `${lpuDoctorsUrl}#tab-content`
})
const lpuNotWorkingText = computed(() => {
  const expressDateTime = context.nearest_appointment_express_datetime

  if (!expressDateTime) {
    return ''
  }

  const parsedDate = parseISO(expressDateTime)

  if (!isValid(parsedDate)) {
    return ''
  }

  const locale = getDateFnsLocaleByIso(context.current_language.iso)

  return `${tStatic('Клиника закрыта, откроется')} ${format(parsedDate, 'd MMMM', { locale })} ${tStatic('в')} ${format(parsedDate, 'HH:mm')}.`
})

const {
  slotsByDoctorId,
  isLoading: isSlotsLoading,
  loadSlots,
} = useDoctorsListSchedule({
  lpuId: props.lpu.id,
  townTimedeltaCandidates: [
    context.town.timedelta,
    props.lpu.town.timedelta,
  ],
  doctors,
})

function getCardDoctorUrl(doctor: LpuDoctor) {
  return getDoctorPageUrl(reverseFullL10N, {
    language: urlLang,
    townTranslit: props.lpu.town.translit,
    doctorTranslit: doctor.translit,
  })
}

onMounted(() => {
  loadSlots()
})
</script>
